/**
 * DailyPuzzleManager - Daily Puzzle & Retention Engine ("Hand of the Day")
 * 
 * Features:
 * 1. Daily Discard Challenge:
 *    - Seeded deterministically by calendar date (YYYY-MM-DD).
 *    - All players get dealt the same 6 cards and role (Dealer vs Pone).
 *    - Evaluated against optimal Hand Expected Value (EV) across all 46 cuts.
 * 2. Daily Pegging Scenario:
 *    - Realistic endgame and midgame pegging puzzles.
 *    - Evaluates winning tactical card play vs traps with deep strategic breakdowns.
 * 3. Daily Streaks & Viral Social Sharing:
 *    - Tracks daily completion, current streak, best streak, and grade stats.
 *    - Generates 1-click Wordle-style emoji share text for clipboard.
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { DiscardTrainer } from './DiscardTrainer.js';
import { VAL } from '../scoring/ScoringConstants.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { Profile } from '../profile/Profile.js';

// Anchor date for Day # counter (e.g. Day 1 on 2026-01-01)
const ANCHOR_DATE = new Date('2026-01-01T00:00:00Z');

// Bank of curated, high-stakes tactical pegging scenarios
export const PEGGING_SCENARIOS = [
  {
    id: 'endgame_116_118',
    title: 'The 31-Trap Squeeze',
    subtitle: 'Endgame Match Point',
    description: 'You are Pone at 116 points. Dealer has 118 points. Game target is 121. The count is at 21 after Dealer played a 10. Opponent holds exactly one card remaining (a 6♥). What card do you play to guarantee the win before Dealer can peg out?',
    playerScore: 116,
    opponentScore: 118,
    targetScore: 121,
    isDealer: false, // Player is Pone
    currentCount: 21,
    countHistory: [
      { card: '5H', label: '5♥', playedBy: 'pone', value: 5, countAfter: 5 },
      { card: '6D', label: '6♦', playedBy: 'dealer', value: 6, countAfter: 11 },
      { card: 'KC', label: 'K♣', playedBy: 'dealer', value: 10, countAfter: 21 }
    ],
    playerHand: [
      { id: '4S', r: 4, s: 'S' },
      { id: '5D', r: 5, s: 'D' },
      { id: '7C', r: 7, s: 'C' }
    ],
    opponentKnown: '6♥ (Dealer holds 6♥)',
    optimalCardId: '4S',
    solutions: {
      '4S': {
        correct: true,
        grade: 'SOLVED',
        badge: '🎯 MASTER MOVE',
        explanation: 'Brilliant! Playing the 4♠ brings the count to exactly 25. Dealer holds 6♥, so playing it would make 31 for 2 pts, BUT 25 + 6 = 31! Dealer cannot play anything else and you claim 31 for 2 pts or if Dealer plays 6♥, count is 31 for Dealer? Wait! If count is 25, 25+6 = 31! With count at 25, Dealer MUST play 6♥ for 31 for 2 pts (120). Then Dealer has NO cards left, the count resets to 0, and you play your 7♣ and 5♦ for 1 point "Go" and peg out to 121 for the win!'
      },
      '5D': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ BLUNDER',
        explanation: 'Fatal trap! Playing 5♦ brings the count to 26. Dealer can no longer play their 6 (26 + 6 = 32 > 31) and says "Go". You score 1 pt for 31/Go (117). But on the fresh count from 0, Dealer leads their 6♥, you must play 7 (count 13) or 4 (count 10), and Dealer scores the final "Go" to reach 119 and secures dealer crib first!'
      },
      '7C': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ BLUNDER',
        explanation: 'Disaster! Playing 7♣ brings the count to 28. Neither player can play. You get 1 pt for Go (117). But on the fresh count, Dealer holds their 6♥ and controls tempo to win the game.'
      }
    }
  },
  {
    id: 'run_trap_24',
    title: 'The Stepping Stone Run',
    subtitle: 'Trap & Run Counter',
    description: 'You are Dealer at 117 points. Pone has 119 points. The count is 17. The play sequence so far is 8♠ then 9♦. Pone has just said "Go". What card do you drop to secure maximum consecutive pegging points?',
    playerScore: 117,
    opponentScore: 119,
    targetScore: 121,
    isDealer: true,
    currentCount: 17,
    countHistory: [
      { card: '8S', label: '8♠', playedBy: 'pone', value: 8, countAfter: 8 },
      { card: '9D', label: '9♦', playedBy: 'dealer', value: 9, countAfter: 17 }
    ],
    playerHand: [
      { id: '7H', r: 7, s: 'H' },
      { id: '10C', r: 10, s: 'C' },
      { id: '2S', r: 2, s: 'S' }
    ],
    opponentKnown: 'Pone is out of legal plays (called "Go")',
    optimalCardId: '7H',
    solutions: {
      '7H': {
        correct: true,
        grade: 'SOLVED',
        badge: '🌟 PERFECT RUN',
        explanation: 'Masterful! Playing 7♥ brings the count to 24 (17 + 7) and completes the 3-card run 8-9-7 for 3 pegging points! Since Pone called Go, you can then also play your 2♠ to hit 26 and take the 1 pt Go for 4 total points, vaulting you straight from 117 to 121 for the instant win!'
      },
      '10C': {
        correct: false,
        grade: 'SUBOPTIMAL',
        badge: '⚠️ MISSED WIN',
        explanation: 'Playing 10♣ brings the count to 27 and scores 3 points for the run 8-9-10! However, with count at 27 you cannot also play your 2♠ (27+2=29), leaving you 1 point short of the 121 match target.'
      },
      '2S': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ BLUNDER',
        explanation: 'Playing 2♠ brings the count to 19 without completing any run or sequence, scoring 0 points and failing to reach 121.'
      }
    }
  },
  {
    id: 'fifteen_lead_defense',
    title: 'The Poison 5 Defense',
    subtitle: 'Opening Play Strategy',
    description: 'First hand of the match. You are Pone holding 5♠, 7♦, 8♣, K♥. You need to lead a card. Opponent is a sharp Shark AI. Which card is the statistically safest lead that minimizes opponent scoring opportunities?',
    playerScore: 0,
    opponentScore: 0,
    targetScore: 121,
    isDealer: false,
    currentCount: 0,
    countHistory: [],
    playerHand: [
      { id: '5S', r: 5, s: 'S' },
      { id: '7D', r: 7, s: 'D' },
      { id: '8C', r: 8, s: 'C' },
      { id: 'KH', r: 13, s: 'H' }
    ],
    opponentKnown: 'Dealer holds 4 unseen cards',
    optimalCardId: '7D',
    solutions: {
      '7D': {
        correct: true,
        grade: 'SOLVED',
        badge: '🛡️ FLAWLESS DEFENSE',
        explanation: 'Correct! Leading a 7 is mathematically standard defense. If opponent plays an 8 for 15-for-2, you can counter with your 8♣ or 7♦ to score a pair or run! Leading from a 7-8 connector retains strong offensive counters while avoiding 10-point immediate 15s.'
      },
      '8C': {
        correct: false,
        grade: 'SUBOPTIMAL',
        badge: '⚠️ RISKY PLAY',
        explanation: 'Leading an 8 allows Dealer to play 7 for 15-2. While you can follow with 7♦, 7 is generally preferred as an opening lead over 8 because fewer cards hit exactly 15 off an 8 without setting up runs.'
      },
      '5S': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ GOLDEN RULE BROKEN',
        explanation: 'Never lead a 5! With sixteen 10-value cards in a 52-card deck (nearly 1 in 3 cards), leading a 5 gives the dealer an easy 15-for-2 over 70% of the time.'
      },
      'KH': {
        correct: false,
        grade: 'MISTAKE',
        badge: '⚠️ SUBOPTIMAL',
        explanation: 'Leading a King allows opponent to play a 5 for 15-for-2. Furthermore, leading face cards leaves you with fewer flexible responses on subsequent cards.'
      }
    }
  },
  {
    id: 'stinkhole_defense_120',
    title: 'The Stink Hole Escape',
    subtitle: 'Precision 1-Point Peg',
    description: 'You are at 120 points (Stink Hole) as Dealer! Opponent is at 120. Game target is 121. The count is 29 after Opponent played a 9. What card do you play to score the single point needed to win the match right now?',
    playerScore: 120,
    opponentScore: 120,
    targetScore: 121,
    isDealer: true,
    currentCount: 29,
    countHistory: [
      { card: '10S', label: '10♠', playedBy: 'pone', value: 10, countAfter: 10 },
      { card: '10D', label: '10♦', playedBy: 'dealer', value: 10, countAfter: 20 },
      { card: '9C', label: '9♣', playedBy: 'pone', value: 9, countAfter: 29 }
    ],
    playerHand: [
      { id: '2H', r: 2, s: 'H' },
      { id: 'AH', r: 1, s: 'H' },
      { id: '6S', r: 6, s: 'S' }
    ],
    opponentKnown: 'Count is 29. Single point wins.',
    optimalCardId: '2H',
    solutions: {
      '2H': {
        correct: true,
        grade: 'SOLVED',
        badge: '🏆 31 FOR THE WIN',
        explanation: 'Perfection! 29 + 2 = exactly 31! Playing 2♥ hits 31 for 2 points, giving you the immediate winning peg from 120 to 121+ and ending the game on the spot!'
      },
      'AH': {
        correct: false,
        grade: 'SUBOPTIMAL',
        badge: '⚠️ DANGEROUS MISS',
        explanation: 'Playing A♥ brings the count to 30. If Opponent has an Ace remaining, they will play it for 31-for-2 and steal the match! Never leave count at 30 when you have the direct 31 play.'
      },
      '6S': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ ILLEGAL PLAY',
        explanation: 'Playing 6♠ exceeds 31 (29 + 6 = 35) and is an illegal move.'
      }
    }
  },
  {
    id: 'pone_pair_counter',
    title: 'The Bait and Trap',
    subtitle: 'Tactical Pairing Trap',
    description: 'You are Pone at 112 points, Dealer is at 114. You led 4♣. Dealer immediately paired your 4 with 4♦ (count 8, Dealer pegged 2). What do you play now?',
    playerScore: 112,
    opponentScore: 116,
    targetScore: 121,
    isDealer: false,
    currentCount: 8,
    countHistory: [
      { card: '4C', label: '4♣', playedBy: 'pone', value: 4, countAfter: 4 },
      { card: '4D', label: '4♦', playedBy: 'dealer', value: 4, countAfter: 8 }
    ],
    playerHand: [
      { id: '4S', r: 4, s: 'S' },
      { id: '7H', r: 7, s: 'H' },
      { id: '9C', r: 9, s: 'C' }
    ],
    opponentKnown: 'Dealer holds 3 unseen cards',
    optimalCardId: '4S',
    solutions: {
      '4S': {
        correct: true,
        grade: 'SOLVED',
        badge: '⚡ TRIPLE 4 BOMB',
        explanation: 'Boom! Playing your 4♠ makes three-of-a-kind (Pair Royal) for 6 pegging points! Count becomes 12. Dealer cannot 4-of-a-kind you because the fourth 4 is in the crib/deck. You vault 6 points ahead!'
      },
      '7H': {
        correct: false,
        grade: 'SUBOPTIMAL',
        badge: '⚠️ MISSED 6 POINTS',
        explanation: 'Playing 7♥ brings the count to 15 for 2 points, but forfeits the devastating 6-point Pair Royal with your 4♠.'
      },
      '9C': {
        correct: false,
        grade: 'MISTAKE',
        badge: '❌ BLUNDER',
        explanation: 'Playing 9♣ scores 0 points and wastes a huge scoring opportunity.'
      }
    }
  }
];

export class DailyPuzzleManager {
  /**
   * Generates a deterministic 32-bit integer hash from a date string (e.g. "2026-09-23").
   * @param {string} str
   * @returns {number}
   */
  static hashString(str) {
    let hash = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
      hash = Math.imul(hash ^ str.charCodeAt(i), 3432918353);
      hash = (hash << 13) | (hash >>> 19);
    }
    return hash >>> 0;
  }

  /**
   * Mulberry32 PRNG generator seeded with integer seed.
   * @param {number} a
   * @returns {function(): number} PRNG returning [0, 1)
   */
  static mulberry32(a) {
    return function() {
      let t = (a += 0x6D2B79F5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * Gets calendar day key in YYYY-MM-DD format (local time).
   * @param {Date} [d=new Date()]
   * @returns {string}
   */
  static getDailyDateKey(d = new Date()) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Calculates Day # counter from anchor date.
   * @param {string} dateKey YYYY-MM-DD
   * @returns {number}
   */
  static getDayNumber(dateKey) {
    const target = new Date(`${dateKey}T00:00:00Z`);
    const diffMs = target.getTime() - ANCHOR_DATE.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, days);
  }

  /**
   * Generates deterministic 6-card hand for the specified dateKey.
   * @param {string} [dateKey]
   * @returns {{ hand: Card[], isDealer: boolean, dayNumber: number, dateKey: string }}
   */
  static getDailyHand(dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const seed = DailyPuzzleManager.hashString(`cribbage-daily-hand-${dateKey}`);
    const rng = DailyPuzzleManager.mulberry32(seed);

    // Make standard 52-card deck
    const deck = Deck.makeDeck(1, false);

    // Fisher-Yates shuffle using deterministic rng
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const temp = deck[i];
      deck[i] = deck[j];
      deck[j] = temp;
    }

    const hand = deck.slice(0, 6);
    // Role alternates by day number (even = Dealer, odd = Pone)
    const dayNumber = DailyPuzzleManager.getDayNumber(dateKey);
    const isDealer = (dayNumber % 2 === 0);

    return { hand, isDealer, dayNumber, dateKey };
  }

  /**
   * Selects deterministic pegging puzzle scenario for the specified dateKey.
   * @param {string} [dateKey]
   * @returns {Object} Scenario definition with Card instances in playerHand
   */
  static getDailyPeggingScenario(dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const dayNumber = DailyPuzzleManager.getDayNumber(dateKey);
    const index = (dayNumber - 1) % PEGGING_SCENARIOS.length;
    const baseScenario = PEGGING_SCENARIOS[index];

    // Instantiate Card objects for player hand
    const playerHand = baseScenario.playerHand.map(c => new Card({ r: c.r, s: c.s, id: c.id }));

    return {
      ...baseScenario,
      dayNumber,
      dateKey,
      playerHand
    };
  }

  /**
   * Evaluates player's chosen discards for the daily challenge.
   * @param {string[]} chosenCardIds Array of 2 card IDs e.g. ['5H', '6D']
   * @param {string} [dateKey]
   * @returns {Object} Evaluation report
   */
  static evaluateDailyDiscard(chosenCardIds, dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const { hand, isDealer, dayNumber } = DailyPuzzleManager.getDailyHand(dateKey);
    const analysis = DiscardTrainer.analyzeHand(hand, isDealer);
    const chosenSet = new Set(chosenCardIds);
    const userCombos = analysis.combos.filter(c => c.disc.every(card => chosenSet.has(card.id)));

    if (!userCombos || userCombos.length === 0) {
      throw new Error('Selected cards do not form a valid 2-card discard from hand.');
    }

    const userCombo = userCombos[0];
    const optimalCombo = analysis.optimal;
    const diff = Number((optimalCombo.netEV - userCombo.netEV).toFixed(2));

    let grade = 'optimal';
    let gradeLabel = '🌟 PERFECT OPTIMAL';
    if (diff > 0.00 && diff <= 0.50) {
      grade = 'great';
      gradeLabel = '🟢 EXCELLENT PLAY';
    } else if (diff > 0.50 && diff <= 1.50) {
      grade = 'good';
      gradeLabel = '🟡 GOOD CHOICE';
    } else if (diff > 1.50) {
      grade = 'suboptimal';
      gradeLabel = '🔴 SUBOPTIMAL';
    }

    return {
      dateKey,
      dayNumber,
      isDealer,
      userCombo,
      optimalCombo,
      diff,
      grade,
      gradeLabel,
      analysis
    };
  }

  /**
   * Evaluates player's pegging card selection for the daily pegging puzzle.
   * @param {string} chosenCardId e.g. '4S'
   * @param {string} [dateKey]
   * @returns {Object}
   */
  static evaluateDailyPegging(chosenCardId, dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const scenario = DailyPuzzleManager.getDailyPeggingScenario(dateKey);
    const solution = scenario.solutions[chosenCardId];

    if (!solution) {
      throw new Error(`Invalid card selected for scenario: ${chosenCardId}`);
    }

    return {
      dateKey,
      dayNumber: scenario.dayNumber,
      chosenCardId,
      scenario,
      isCorrect: solution.correct,
      solution
    };
  }

  /**
   * Loads user's daily puzzle statistics and completion state from persistent storage.
   * @param {Object} [storage]
   * @returns {Object}
   */
  static loadStats(storage = (typeof window !== 'undefined' ? window.appStorage : null)) {
    const defaultStats = {
      currentStreak: 0,
      bestStreak: 0,
      lastPlayedDate: '',
      history: {}, // dateKey -> { discard: {...}, pegging: {...} }
      totalPlayed: 0,
      perfectDiscards: 0,
      peggingWins: 0
    };

    let loaded = null;
    if (storage) {
      try {
        const raw = storage.getItem('cc_daily_puzzle_stats');
        if (raw) loaded = JSON.parse(raw);
      } catch (e) {}
    }

    // Also check User Profile if storage key wasn't found or was empty
    if (!loaded) {
      try {
        const profile = (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function')
          ? window.Profile.getInstance(storage)
          : (Profile ? Profile.getInstance(storage) : null);
        if (profile && typeof profile.getDailyPuzzleStats === 'function') {
          const pStats = profile.getDailyPuzzleStats(storage);
          if (pStats && (pStats.totalPlayed > 0 || pStats.currentStreak > 0 || Object.keys(pStats.history || {}).length > 0)) {
            loaded = pStats;
          }
        }
      } catch (_) {}
    }

    if (!loaded && DailyPuzzleManager._memoryStats) {
      loaded = DailyPuzzleManager._memoryStats;
    }

    const merged = { ...defaultStats, ...(loaded || {}) };
    DailyPuzzleManager._memoryStats = merged;
    return merged;
  }

  /**
   * Saves updated statistics to persistent storage and synchronizes into User Profile.
   * @param {Object} stats
   * @param {Object} [storage]
   */
  static saveStats(stats, storage = (typeof window !== 'undefined' ? window.appStorage : null)) {
    DailyPuzzleManager._memoryStats = { ...stats };
    if (storage) {
      try {
        storage.setItem('cc_daily_puzzle_stats', JSON.stringify(stats));
      } catch (e) {
        console.warn('[DailyPuzzleManager] Error saving stats:', e);
      }
    }
    // Synchronize into User Profile
    try {
      const profile = (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function')
        ? window.Profile.getInstance(storage)
        : (Profile ? Profile.getInstance(storage) : null);
      if (profile && typeof profile.setDailyPuzzleStats === 'function') {
        profile.setDailyPuzzleStats(stats, storage);
      }
    } catch (e) {
      console.warn('[DailyPuzzleManager] Error syncing stats to Profile:', e);
    }
  }

  /**
   * Resets daily puzzle statistics and streak history.
   * @param {Object} [storage]
   * @returns {Object} Reset default statistics
   */
  static resetStats(storage = (typeof window !== 'undefined' ? window.appStorage : null)) {
    const defaultStats = {
      currentStreak: 0,
      bestStreak: 0,
      lastPlayedDate: '',
      history: {},
      totalPlayed: 0,
      perfectDiscards: 0,
      peggingWins: 0
    };
    DailyPuzzleManager.saveStats(defaultStats, storage);
    try {
      const profile = (typeof window !== 'undefined' && window.Profile && typeof window.Profile.getInstance === 'function')
        ? window.Profile.getInstance(storage)
        : (Profile ? Profile.getInstance(storage) : null);
      if (profile && typeof profile.clearDailyPuzzleStats === 'function') {
        profile.clearDailyPuzzleStats(storage);
      }
    } catch (_) {}
    return defaultStats;
  }

  /**
   * Records completion of either discard or pegging section.
   * Updates streaks if both daily challenges are finished for today.
   * @param {'discard'|'pegging'} section
   * @param {Object} result
   * @param {string} [dateKey]
   * @returns {Object} Updated stats
   */
  static recordCompletion(section, result, dateKey = DailyPuzzleManager.getDailyDateKey(), options = {}) {
    const storage = options.storage || (typeof window !== 'undefined' ? window.appStorage : null);
    const stats = DailyPuzzleManager.loadStats(storage);
    if (!stats.history[dateKey]) {
      stats.history[dateKey] = {};
    }

    const todayEntry = stats.history[dateKey];
    todayEntry[section] = result;

    if (section === 'discard' && result.grade === 'optimal') {
      stats.perfectDiscards = (stats.perfectDiscards || 0) + 1;
      try {
        AchievementManager.incrementStat('dailyDiscardOptimal', 1, options);
      } catch (e) {}
    }
    if (section === 'pegging' && result.isCorrect) {
      stats.peggingWins = (stats.peggingWins || 0) + 1;
      try {
        AchievementManager.incrementStat('dailyPeggingCorrect', 1, options);
      } catch (e) {}
    }

    // Check if both challenges are done today to update streak
    if (todayEntry.discard && todayEntry.pegging && todayEntry.completed !== true) {
      todayEntry.completed = true;
      stats.totalPlayed = (stats.totalPlayed || 0) + 1;

      // Calculate streak
      const lastDate = stats.lastPlayedDate;
      if (!lastDate) {
        stats.currentStreak = 1;
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayKey = DailyPuzzleManager.getDailyDateKey(yesterday);

        if (lastDate === yesterdayKey) {
          stats.currentStreak = (stats.currentStreak || 0) + 1;
        } else if (lastDate === dateKey) {
          // Already played today
        } else {
          stats.currentStreak = 1; // Streak broken
        }
      }

      stats.lastPlayedDate = dateKey;
      if (stats.currentStreak > (stats.bestStreak || 0)) {
        stats.bestStreak = stats.currentStreak;
      }
    }

    DailyPuzzleManager.saveStats(stats, storage);
    try {
      AchievementManager.checkDailyStreaks(stats, options);
    } catch (e) {}
    return stats;
  }

  /**
   * Generates viral Wordle-style shareable text for clipboard.
   * @param {string} [dateKey]
   * @returns {string}
   */
  static generateShareText(dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const stats = DailyPuzzleManager.loadStats();
    const dayNumber = DailyPuzzleManager.getDayNumber(dateKey);
    const today = stats.history[dateKey] || {};

    let discardStr = '⏳ Not completed';
    if (today.discard) {
      const g = today.discard.grade;
      if (g === 'optimal') discardStr = '🌟 PERFECT (0.00 loss)';
      else if (g === 'great') discardStr = `🟢 EXCELLENT (${today.discard.diff} loss)`;
      else if (g === 'good') discardStr = `🟡 GOOD (${today.discard.diff} loss)`;
      else discardStr = `🔴 SUBOPTIMAL (${today.discard.diff} loss)`;
    }

    let peggingStr = '⏳ Not completed';
    if (today.pegging) {
      peggingStr = today.pegging.isCorrect ? '🎯 SOLVED' : '❌ FAILED';
    }

    const streak = stats.currentStreak || 1;

    return `Crazy Cribbage Daily #${dayNumber} 📅\n` +
           `🃏 Discard: ${discardStr}\n` +
           `⚡ Pegging: ${peggingStr}\n` +
           `🔥 Streak: ${streak} ${streak === 1 ? 'Day' : 'Days'}\n` +
           `Play Free: https://customcribboards.com/pages/crazy-cribbage`;
  }

  /**
   * Checks whether both daily puzzles have been completed for today.
   * @param {string} [dateKey]
   * @returns {boolean}
   */
  static isTodayCompleted(dateKey = DailyPuzzleManager.getDailyDateKey()) {
    const stats = DailyPuzzleManager.loadStats();
    const entry = stats.history[dateKey];
    return !!(entry && entry.completed);
  }
}

if (typeof window !== 'undefined') {
  window.DailyPuzzleManager = DailyPuzzleManager;
}
